import React from 'react'
import {
    Form, Row, Col, Card, FormikSelect, Table, Button, FormikSimpleSelect, FormikInput, H5, SaveButton, Divider,
    CardWithBreadcrumb
} from '../../Elements'
import { FieldArray } from 'formik'
import { Icon, MenuItem, NonIdealState } from '@blueprintjs/core'
import { useNavigate, Prompt } from 'react-router-dom'
import { isEqual, isEqualWith } from 'lodash'
import { useMediaQuery } from 'react-responsive'

const NEW_COLOR_DEFAULT_STATE = {
    id: -1,
    name: '',
    material_type: '',
    recipe: []
}
const rightAlignTextStyle = {
    textAlign: 'right'
}

export const ColorRecipeForm = ({ values, isCreationMode, setIsCreationMode, errors, setFieldValue, ...props }) => {
    const defaultNewIngredientState = {
        color_id: values.selected_color.id || -1,
    }

    const navigate = useNavigate()
    const pageTitle = isCreationMode ? 'Color Creation' : 'Color'
    const cancelButtonText = isCreationMode ? 'Cancel Color Creation' : 'Cancel'

    const enterCreationMode = () => {
        setFieldValue('selected_color', NEW_COLOR_DEFAULT_STATE)
        setIsCreationMode(true)
    }

    const handleClickCancel = () => {
        if (isCreationMode) {
            setFieldValue('selected_color', '')
            setIsCreationMode(false)
        } else {
            navigate('/')
        }
    }

    const formatColorSelectItemName = (item) => {
        const { name: productName, material_type } = item
        const materialName = material_type && material_type.name
        return `${materialName ? `${materialName}  –  ` : ''}${productName}`
    }

    const customColorSelectItemRenderer = (item, { handleClick, modifiers }) => (
        <MenuItem
            active={modifiers.active}
            key={item.id}
            text={formatColorSelectItemName(item)}
            onClick={(e) => handleClick(item, e)}
        />
    )
    const isMobile = useMediaQuery({ query: '(max-width: 800px)' })
    const unchangedTargetedColor = values.lookupPebbleColor.find(c => values.selected_color && values.selected_color.id === c.id)
    return <Form>
        <CardWithBreadcrumb items={[{ text: 'Color Recipes', icon: 'tint' }]} >
            <Button intent='danger' icon='disable' text={!isMobile && cancelButtonText} onClick={() => handleClickCancel()} />
            <SaveButton disabled={!values.selected_color} isMobile={isMobile} />
            {!isCreationMode &&
                <Button intent='primary' text={!isMobile && 'Create New Color'} icon='lab-test' onClick={() => enterCreationMode()} />
            }
        </CardWithBreadcrumb>
        <Prompt
            when={!props.isSubmitting && values.selected_color && !isEqualWith(values.selected_color, unchangedTargetedColor, (newValue, oldValue, key) => {
                if (isCreationMode) return isEqual(newValue, NEW_COLOR_DEFAULT_STATE)
                if (key === 'quantity') return newValue.toString() === oldValue.toString()
            })}
            message={'You have unsaved changes. Navigate away?'}
        />
        <Card>
            <Row>
                <Col lg={12}>
                    <H5>{pageTitle}</H5>
                    <Col lg={6} xs={6} style={{ 'float': 'right' }}>

                    </Col>
                    <Col lg={6} xs={12}>
                        {!isCreationMode &&
                            <FormikSelect
                                disableMaxListSize
                                name='selected_color'
                                items={values.lookupPebbleColor}
                                customItemRenderer={customColorSelectItemRenderer}
                            />
                        }
                        {values.selected_color &&
                            <FormikInput
                                key={`${values.selected_color.name}`}
                                name='selected_color.name'
                                label='Name'
                            />
                        }
                        {values.selected_color &&
                            <FormikSimpleSelect
                                name='selected_color.material_type'
                                label='Material Type'
                                items={values.lookupPebbleColorMaterialType}
                                nullable
                            />
                        }
                    </Col>
                </Col>
            </Row>
            <Divider style={{ marginBottom: '2em' }} />
            <Row>
                <Col lg={12}>
                    {!values.selected_color &&
                        <NonIdealState
                            icon='tint'
                            title='Select a color to get started!'
                        />
                    }
                    {values.selected_color &&
                        <>
                            <H5>Ingredients</H5>
                            <FieldArray
                                name={`selected_color.recipe`}
                                render={({ push, remove }) =>
                                    !isMobile
                                        ? <Table
                                            style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        Product
                                                    </th>
                                                    <th>
                                                        Quantity
                                                    </th>
                                                    <th style={rightAlignTextStyle}>
                                                        <Button icon='plus' intent='primary' text='Add' onClick={() => push(defaultNewIngredientState)} />
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {values.selected_color && values.selected_color.recipe && values.selected_color.recipe.map((entry, idx) => {
                                                    return (
                                                        <tr key={`selected_color.recipe-${idx}`}>
                                                            <td>
                                                                <FormikSimpleSelect
                                                                    key={`product-${idx}-${entry.product}`}
                                                                    name={`selected_color.recipe.${idx}.product`}
                                                                    items={values.lookupProductsForPebbleColorRecipes}
                                                                    nullable
                                                                />
                                                            </td>
                                                            <td>
                                                                <FormikInput
                                                                    key={`quantity-${idx}-${entry.quantity}`}
                                                                    name={`selected_color.recipe.${idx}.quantity`}
                                                                />
                                                            </td>
                                                            <td style={rightAlignTextStyle}>
                                                                <Icon icon='delete' iconSize='25' intent='danger' onClick={() => remove(idx)} />
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                        : <>
                                            <Row>
                                                <Col xs={7}>
                                                    <span style={{ fontWeight: 'bold' }}>Product</span>
                                                </Col>
                                                <Col xs={3}>
                                                    <span style={{ fontWeight: 'bold' }}>Quantity</span>
                                                </Col>
                                                <Col xs={2} style={rightAlignTextStyle}>
                                                    <Icon icon='add' intent='primary' iconSize='20' onClick={() => push(defaultNewIngredientState)} />
                                                </Col>
                                            </Row>
                                            <Divider style={{ marginBottom: '1em' }} />
                                            {values.selected_color && values.selected_color.recipe && values.selected_color.recipe.map((entry, idx) => {
                                                return <Row>
                                                    <Col xs={7}>
                                                        <FormikSimpleSelect
                                                            key={`selected_color.recipe-${idx}-product`}
                                                            name={`selected_color.recipe.${idx}.product`}
                                                            items={values.lookupProductsForPebbleColorRecipes}
                                                            nullable
                                                        />
                                                    </Col>
                                                    <Col xs={3}>
                                                        <FormikInput
                                                            key={`selected_color.recipe-${idx}-quantity`}
                                                            name={`selected_color.recipe.${idx}.quantity`}
                                                        />
                                                    </Col>
                                                    <Col xs={2} style={rightAlignTextStyle}>
                                                        <Icon icon='delete' iconSize='20' intent='danger' onClick={() => remove(idx)} />
                                                    </Col>
                                                </Row>
                                            })}
                                        </>
                                }
                            />
                        </>
                    }
                </Col>
            </Row>
        </Card>
    </Form>
}